<script lang="ts">
import PolicyDetails from '@/views/policy/PolicyDetails.vue';
import ManagedDateRange from '@/components/ManagedDateRange.vue';
import SelectSearch from '@/components/v2/SelectSearch.vue';

export default {
  name: 'PolicyList',
  components: { PolicyDetails, ManagedDateRange, SelectSearch },
};

interface DatePayload {
  startDate?: Date;
  endDate?: Date;
}
</script>

<script setup lang="ts">
import { ref, computed, onBeforeMount, onMounted } from 'vue';
import { DateTime } from 'luxon';
import { useRoute } from 'vue-router';
import { registerResizeItem } from '@/utilities/resize';
import { policyDetailsType } from '@/types/PolicyDetailsType.js';

const searchFields = [
  { value: 'description', label: 'Description' },
  { value: 'policyNumber', label: 'Policy Number' },
  { value: 'accountName', label: 'Account Name' },
  { value: 'issuingCompany', label: 'Issuing Company' },
  { value: 'servicingRoles', label: 'Servicing Roles' }
];

// -----
// props
// -----
const props = defineProps({
  scenario: {
    type: String,
    validator(value: policyDetailsType) {
      return [
        policyDetailsType.All,
        policyDetailsType.Account,
        policyDetailsType.ParentAccount,
        policyDetailsType.Carrier,
        policyDetailsType.Wholesaler,
        policyDetailsType.CustomWholesaler,
        policyDetailsType.Program,
      ].includes(value);
    },
  }
})

// -----
// refs
// -----
const route = useRoute();
const startDate = ref<Date>(DateTime.local().minus({ days: 366 }).toJSDate());
const endDate = ref<Date>(DateTime.local().minus({ days: 1 }).toJSDate());

const searchQuery = ref<string>('');
const searchField = ref<string>('description');

// ---------
// computed
// ---------
const startDateString = computed(() => DateTime.fromJSDate(startDate.value).toISODate());
const endDateString = computed(() => DateTime.fromJSDate(endDate.value).toISODate());

const isPolicyExplorer = computed(() => {
  return props.scenario === policyDetailsType.All;
});

// -----------
// lifecycles
// -----------
onBeforeMount(() => {
  if (route.query.start) {
    startDate.value = DateTime.fromISO(route.query.start as string).toJSDate();
  }
  if (route.query.end) {
    endDate.value = DateTime.fromISO(route.query.end as string).toJSDate();
  }
});

onMounted(() => {
  registerResizeItem({ name: 'policy-list-header', selector: 'policy-list-header' });
});

// -------
// methods
// -------
function setDate(datePayload: DatePayload) {
  if (datePayload.startDate) {
    startDate.value = datePayload.startDate;
  }
  if (datePayload.endDate) {
    endDate.value = datePayload.endDate;
  }
}

function handleClearSearchFilter() {
  searchQuery.value = '';
}
</script>

<template>
  <div class="tw:pt-4">
    <div v-if="isPolicyExplorer" id="policy-list-header" class="tw:flex tw:justify-between tw:px-4 tw:pb-2 tw:pt-1">
      <h1>Policy Explorer</h1>

      <SelectSearch
        class="tw:ml-auto tw:mr-4 tw:w-full tw:max-w-[500px]"
        :options="searchFields"
        v-model:query="searchQuery"
        v-model:selected-option="searchField" />

      <ManagedDateRange v-if="isPolicyExplorer" :initialStartDate="startDate" :initialEndDate="endDate" :onApply="setDate" />
    </div>

    <PolicyDetails
      :scenario="props.scenario"
      :startDate="startDateString"
      :endDate="endDateString"
      :showFilterToggles="true"
      :show-toggles-on-table="true"
      :search-field="searchField"
      :search-query="searchQuery"
      @clear-filters="handleClearSearchFilter"
      :show-more-filters="isPolicyExplorer">
        <template v-if="!isPolicyExplorer" v-slot:header-right>
          <SelectSearch
              class="tw:ml-auto tw:mr-4 tw:w-full tw:max-w-[500px]"
              :options="searchFields"
              v-model:query="searchQuery"
              v-model:selected-option="searchField" />
        </template>
    </PolicyDetails>
  </div>
</template>
